import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RinkDiagram } from "../components/RinkDiagram";
import type { Drill } from "../domain/drill";
import { deleteCloudDrill } from "../lib/cloud-drill-storage";
import { deleteDrill, loadDrills } from "../lib/drill-storage";

export function LibraryRoute() {
  const [drills, setDrills] = useState<Drill[]>(() => loadDrills());
  const [query, setQuery] = useState("");
  const visible = useMemo(
    () =>
      drills.filter((drill) =>
        `${drill.title} ${drill.objective} ${drill.ageGroups.join(" ")}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [drills, query],
  );

  const remove = (drill: Drill) => {
    if (window.confirm(`Delete “${drill.title}” from your drill library?`)) {
      setDrills(deleteDrill(drill.id));
      void deleteCloudDrill(drill.id);
    }
  };

  return (
    <main className="library-page">
      <section className="library-hero">
        <div>
          <p className="eyebrow">Your coaching workspace</p>
          <h1>
            Build drills.
            <br />
            <span>Own your practice.</span>
          </h1>
          <p>
            Draw clear hockey drills, capture the coaching detail, and build a
            library ready for faster practice planning.
          </p>
        </div>
        <div className="library-hero-actions">
          <Link className="secondary-button large" to="/practice">
            Plan a practice
          </Link>
          <Link className="primary-button large" to="/drills/new">
            + Create a drill
          </Link>
        </div>
      </section>

      <section className="library-content">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Drill library</p>
            <h2>
              {drills.length
                ? `${drills.length} saved ${drills.length === 1 ? "drill" : "drills"}`
                : "Your first drill starts here"}
            </h2>
          </div>
          {drills.length > 0 && (
            <label className="search-field">
              <span>Search</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Name, objective or age..."
              />
            </label>
          )}
        </div>

        {!drills.length ? (
          <div className="empty-library">
            <div className="empty-rink">
              <RinkDiagram template="half" elements={[]} />
            </div>
            <p className="eyebrow">Nothing saved yet</p>
            <h3>Turn the drills in your head into a reusable library.</h3>
            <p>
              Choose a rink, place players and equipment, draw movement paths,
              then add the instructions you need on the ice.
            </p>
            <Link className="primary-button" to="/drills/new">
              Draw your first drill
            </Link>
          </div>
        ) : visible.length ? (
          <div className="drill-grid">
            {visible.map((drill) => (
              <article className="drill-card" key={drill.id}>
                <Link className="drill-preview" to={`/drills/${drill.id}/edit`}>
                  <RinkDiagram
                    template={drill.rink}
                    rinkSide={drill.rinkSide}
                    elements={drill.elements}
                  />
                </Link>
                <div className="drill-card-body">
                  <div className="card-meta">
                    <span>{drill.durationMinutes} min</span>
                    <span>{drill.ageGroups.join(" · ")}</span>
                  </div>
                  <h3>
                    <Link to={`/drills/${drill.id}/edit`}>{drill.title}</Link>
                  </h3>
                  <p>
                    {drill.objective ||
                      "Add an objective to make this drill easier to find later."}
                  </p>
                  <div className="card-actions">
                    <Link to={`/drills/${drill.id}/edit`}>Edit drill →</Link>
                    <button onClick={() => remove(drill)}>Delete</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="no-results">
            <h3>No drills match “{query}”</h3>
            <button className="text-button" onClick={() => setQuery("")}>
              Clear search
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
